import { useApolloClient } from '@apollo/client'
import type { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import {
    BestPracticeQuery,
    GetBestPracticesForContentQuery,
} from 'api/graphQL/queries/bestPractices.queries'
import { useCallback, useEffect, useRef, useState } from 'react'

/** Extends BestPracticeData with OM-specific fields returned by getBestPracticesForContent. */
export interface BestPracticeDataForContent extends BestPracticeData {
    iterationId?: string
    latestVersionId?: string
    isUserAddedContentBestPractice?: boolean
}

interface InternalState {
    data: BestPracticeData | BestPracticeDataForContent[] | null
    loading: boolean
    error: boolean
}

interface UseBestPracticeDetailsBase {
    loading: boolean
    error: boolean
    fetch: (force?: boolean) => void
    reset: () => void
}

export interface UseBestPracticeDetailsSingleReturn extends UseBestPracticeDetailsBase {
    data: BestPracticeData | null
}

export interface UseBestPracticeDetailsMultipleReturn extends UseBestPracticeDetailsBase {
    data: BestPracticeDataForContent[] | null
}

interface UseBestPracticeDetailsOptions {
    /** When true, fires fetch on mount and again whenever refetchTick changes. */
    fetchOnMount?: boolean
    /** Bump (e.g. via `requestBPDetailsRefetch`) to force a fresh fetch. */
    refetchTick?: number
}

const initialState: InternalState = {
    data: null,
    loading: false,
    error: false,
}

// Overload: default — returns a single BestPracticeData
export function useBestPracticeDetails(
    contentItemId: string | undefined,
    projectId: string | undefined,
    options?: UseBestPracticeDetailsOptions & { useMultiple?: false }
): UseBestPracticeDetailsSingleReturn

// Overload: OM mode with literal true — returns a list of BestPracticeDataForContent
export function useBestPracticeDetails(
    contentItemId: string | undefined,
    projectId: string | undefined,
    options: UseBestPracticeDetailsOptions & { useMultiple: true }
): UseBestPracticeDetailsMultipleReturn

// Overload: runtime boolean flag — caller must narrow the union
export function useBestPracticeDetails(
    contentItemId: string | undefined,
    projectId: string | undefined,
    options: UseBestPracticeDetailsOptions & { useMultiple: boolean }
): UseBestPracticeDetailsSingleReturn | UseBestPracticeDetailsMultipleReturn

export function useBestPracticeDetails(
    contentItemId: string | undefined,
    projectId: string | undefined,
    {
        fetchOnMount = false,
        refetchTick,
        useMultiple = false,
    }: UseBestPracticeDetailsOptions & { useMultiple?: boolean } = {}
): UseBestPracticeDetailsSingleReturn | UseBestPracticeDetailsMultipleReturn {
    const apolloClient = useApolloClient()
    const [state, setState] = useState<InternalState>(initialState)
    // Ref keeps fetch stable without adding `state` to its dependency array,
    // which would cause infinite re-renders.
    const stateRef = useRef(state)
    stateRef.current = state

    const fetch = useCallback(
        (force = false) => {
            if (!contentItemId || !projectId) return
            // Skip if data is already loaded and this is a manual (non-forced) call.
            // Tick-driven refetches pass force=true to bypass this guard.
            if (!force && stateRef.current.data !== null && !stateRef.current.loading) return

            setState({ data: null, loading: true, error: false })

            if (useMultiple) {
                apolloClient
                    .query<{ getBestPracticesForContent: BestPracticeDataForContent[] | null }>({
                        query: GetBestPracticesForContentQuery,
                        variables: { parentId: contentItemId, projectId },
                        fetchPolicy: 'no-cache',
                    })
                    .then(({ data }) => {
                        setState({
                            data: data?.getBestPracticesForContent ?? null,
                            loading: false,
                            error: false,
                        })
                    })
                    .catch(() => {
                        setState({ data: null, loading: false, error: true })
                    })
            } else {
                apolloClient
                    .query<{ getBestPractice: BestPracticeData | null }>({
                        query: BestPracticeQuery,
                        variables: { parentId: contentItemId, projectId },
                        fetchPolicy: 'no-cache',
                    })
                    .then(({ data }) => {
                        setState({
                            data: data?.getBestPractice ?? null,
                            loading: false,
                            error: false,
                        })
                    })
                    .catch(() => {
                        setState({ data: null, loading: false, error: true })
                    })
            }
        },
        [apolloClient, contentItemId, projectId, useMultiple]
    )

    const reset = useCallback(() => setState(initialState), [])

    useEffect(() => {
        // Tick-driven refetches bypass the stale-data guard; initial mount fetch does not.
        if (fetchOnMount) fetch(refetchTick !== undefined && refetchTick > 0)
    }, [fetchOnMount, fetch, refetchTick])

    return { ...state, fetch, reset } as
        | UseBestPracticeDetailsSingleReturn
        | UseBestPracticeDetailsMultipleReturn
}
